/** @format */

import React, { useRef } from 'react';
import { InputV2, ButtonLoading, SimpleDialog, CustomCaptchaComponent, Icons, PriceLabel } from 'components';
import { useStyles } from './style';
import { validator } from 'myValidation';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useMediaQuery,
  Button,
} from '@mui/material';
import createToast from 'components/Toast/Toast';
import useCustomHook from './hooks';
import {useCurrencyUnit} from 'hooks'

import Services from './services/TransferDelegationServices';
const TransferDelegation = ({ targetDelegation, onCancel = () => { } }) => {
  // -- STATES --//
  const {
    checkingContact,
    setCheckingContact,
    submitting,
    setSubmitting,
    foundUser,
    setFoundUser,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    enteredCaptcha,
    setEnteredCaptcha,
    captchaId,
    setCaptchaId,
  } = useCustomHook();

  //* * HOOKS *//
  const captchaRef = useRef();
  const classes = useStyles();
  const matches = useMediaQuery('(max-width:600px)');
  const methods = useForm();
  const { handleSubmit, reset, control, register, formState: { errors }, watch } = methods;
  const currencyText = useCurrencyUnit(false);

  //* * API Requests */
  const onCheckContact = async (data) => {
    if (!data?.contactInfo || data?.contactInfo?.length == 0) {
      return createToast('خطا', 'نام کاربری یا تلفن همراه کاربر مورد نظر را وارد کنید!', 'danger');
    }
    try {
      const body = {
        captchaAnswer: enteredCaptcha,
        captchaId: captchaId,
      };
      if (validator.isMobilePhone(data?.contactInfo ?? '')) {
        body.phoneNumber = data?.contactInfo;
      } else {
        body.username = data?.contactInfo;
      }
      setCheckingContact(true);
      const r = await Services.checkContactDelegation(body);
      setCheckingContact(false);

      if (r.result) {
        setFoundUser(r.result);
        setIsConfirmModalOpen(true);
      } else {
        createToast('خطا', 'کاربری با این اطلاعات یافت نشد!', 'danger');
      }
      captchaRef?.current?.onMakeCaptcha();
    } catch (error) {
      setCheckingContact(false);
      captchaRef?.current?.onMakeCaptcha();
    }
  };

  const onTransfer = async () => {
    if (!targetDelegation?.delegationId) {
      return createToast('خطا', 'شناسه اعتبار مشخص نشده!', 'danger');
    }
    if (!foundUser?.ssoId) {
      return createToast('خطا', 'شناسه کاربر مورد نظر مشخص نشده!', 'danger');
    }
    setSubmitting(true);
    try {
      const body = {
        delegationId: targetDelegation?.delegationId,
        ssoId: foundUser?.ssoId,
      };
      await Services.transfer(body);

      createToast('موفق', 'انتقال اعتبار با موفقیت انجام شد', 'success');
      handleSubmitEnd();
      onCancel();
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
    }
  };

  //* * Other Functions */
  const handleSubmitEnd = () => {
    setIsConfirmModalOpen(false);
    setFoundUser(null);
    reset();
  };

  const handleCloseModal = () => {
    setIsConfirmModalOpen(false);
    setFoundUser(null);
  };

  //* * Local Variables */

  return (
    <>
      <Typography className={classes.newPermissionHint}>
        در صورت تمایل می‌توانید وجه تفویض شده جاری به خود را به کاربر دیگری انتقال دهید.
      </Typography>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onCheckContact)} className={classes.form}>
          <InputV2
            fullWidth
            errors={errors}
            rules={['required']}
            label="تلفن همراه/نام کاربری مخاطب"
            name="contactInfo"
            register={register}
            control={control}
            englishNumber
            dir="ltr"
          />
          <Grid container wrap={matches ? 'wrap' : 'nowrap'}>
            <InputV2
              label="کد امنیتی"
              forceValue={enteredCaptcha}
              onChangeValue={(e) => {
                const value = e.target.value;
                if (value.length > 5) {
                  e.preventDefault();
                  setEnteredCaptcha(value.slice(0, 5));
                } else {
                  setEnteredCaptcha(value);
                }
              }}
              englishNumber
              rules={['isNumeric']}
              fullWidth
              dir="ltr"
            />
            <CustomCaptchaComponent
              pageTitle="/Delegation - وجه های تفویض شده به من - دیالوگ انتقال به سایرین"
              ref={captchaRef}
              setCaptchaId={setCaptchaId}
              setEnteredCaptcha={setEnteredCaptcha}
              sizeOptions={{ xs: '12', md: '10', lg: '10' }}
            />
          </Grid>
          <Grid
            md={12}
            xs={12}
            lg={12}
            style={{
              display: 'flex',
              flexDirection: 'row-reverse',
              marginTop: '16px',
              gap: '15px',
            }}
          >
            <ButtonLoading
              name="تایید"
              loading={checkingContact}
              variant="contained"
              color="primary"
              type="submit"
            />
            <Button variant="contained" onClick={onCancel} style={{ marginInlineStart: 8 }}>
              انصراف
            </Button>
          </Grid>
        </form>
      </FormProvider>

      {/* Confirm */}
      <SimpleDialog
        title="انتقال وجه تفویض شده با جزئیات زیر انجام گیرد؟"
        open={isConfirmModalOpen}
        icon={<Icons icon="coin" color="#000" size="25" />}
        onClose={handleCloseModal}
        actions={
          <>
            <ButtonLoading
              name="تایید"
              onClick={onTransfer}
              loading={submitting}
              variant="contained"
              color="primary"
            />
            <Button onClick={handleCloseModal} variant="contained">
              انصراف
            </Button>
          </>
        }
      >
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} aria-label="simple table">
            <TableBody>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  نام کاربر مقصد
                </TableCell>
                <TableCell>
                  {`${!foundUser?.given_name && !foundUser?.family_name ? '-' : ''}${foundUser?.given_name ?? ''
                    } ${foundUser?.family_name ?? ''}`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={classes.titleCell}
                  component="th"
                  scope="row"
                  style={{ fontFamily: 'Vazir' }}
                >
                  تلفن همراه کاربر
                </TableCell>
                <TableCell>{foundUser?.phone_number ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  میزان اعتبار
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {targetDelegation?.amount
                      ?  <PriceLabel amount={targetDelegation?.amount} currency={currencyText} />
                      : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  تخصیص‌دهنده به شما
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {targetDelegation?.assignedFrom ?? '-'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  تاریخ انقضا
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {targetDelegation?.expireDate ?? '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </SimpleDialog>
    </>
  );
};

export default TransferDelegation;
